import { useCallback, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import {
  BROKERAGE_PROFILE_INTAKE_API_PATH,
  readIntakeSnapshot,
  writeIntakeSnapshot,
} from "./brokerageProfileIntakeFields"

/** Load saved brokerage profile intake (GET with session snapshot fallback). */
export function useBrokerageProfileIntake({ loadOnMount = true } = {}) {
  const axios = useAuthorizedAxiosClient()
  const [isLoading, setIsLoading] = useState(Boolean(loadOnMount))
  const [intake, setIntake] = useState(null)
  const [submittedAt, setSubmittedAt] = useState(null)

  const applyPayload = useCallback((payload) => {
    if (payload?.submitted && payload?.intake) {
      setIntake(payload.intake)
      setSubmittedAt(payload.submitted_at || null)
      return
    }
    setIntake(null)
    setSubmittedAt(null)
  }, [])

  const loadSavedIntake = useCallback(() => {
    setIsLoading(true)
    return axios
      .get(BROKERAGE_PROFILE_INTAKE_API_PATH, {
        validateStatus: (status) =>
          status === 200 || status === 405 || status === 404,
      })
      .then((res) => {
        if (res.status === 405 || res.status === 404) {
          applyPayload(readIntakeSnapshot())
          return
        }
        const data = res.data
        if (data?.submitted && data?.intake) {
          writeIntakeSnapshot({
            submitted: true,
            submitted_at: data.submitted_at,
            intake: data.intake,
          })
        }
        applyPayload(data)
      })
      .catch(() => {
        applyPayload(readIntakeSnapshot())
      })
      .finally(() => setIsLoading(false))
  }, [axios, applyPayload])

  useEffect(() => {
    if (loadOnMount) {
      loadSavedIntake()
    }
  }, [loadOnMount, loadSavedIntake])

  return {
    isLoading,
    intake,
    submittedAt,
    hasSubmitted: Boolean(intake),
    loadSavedIntake,
    applyPayload,
  }
}
